/**
 * Shopify Forms アプリ (<shopify-forms-embed>) は Shadow DOM (open mode) で出力され、
 * 外部 CSS は内部に届かないが shadowRoot から style 要素を注入することで内部 input/button の
 * 細かいスタイル (border, rounded, padding, font) を contact ページ風に統一する。
 *
 * 色やフォントは ホスト要素経由の CSS 変数 (tailwind.css 内の
 * .hakotsuru-forms-page-apps shopify-forms-embed { --button-background-color: ... } など)
 * で別途上書きしているため、 ここでは tag selector ベースの構造調整 (枠線・角丸・余白等) のみ
 * 担当する。
 *
 * 注: Shadow DOM 内の class 名は CSS Modules でハッシュ化 (_formInputField_nag3b_7 など)
 * されているため class セレクタは使えない。 input/textarea/select/button のタグセレクタで
 * 広めに当てつつ、 button[type="submit"] のような属性セレクタで送信ボタンを特定する。
 */
(function () {
  'use strict';

  var INJECTED = '__hakotsuru-forms-style-injected';

  var CSS = [
    /* フォーム全体の最大幅を 632px に。 Shopify Forms の内部構造は
         div._appEmbedRoot > div._container > div > section._inline > section._formContainer
         > div._gridItem > form._formFieldset
       の階層で、 form の親 _formContainer / _gridItem が 450px に絞られているため、
       form 自身に max-width: 632 を当てても効果がない。 class はハッシュ化されている
       (例: _formContainer_1q1d2_30) のため、 部分一致 attribute selector で狙う。 */
    'form, form * {',
    '  box-sizing: border-box !important;',
    '}',
    'section[class*="_formContainer"], section[class*="_inline"] [class*="_gridItem"], form {',
    '  max-width: 632px !important;',
    '  width: 100% !important;',
    '  margin-left: auto !important;',
    '  margin-right: auto !important;',
    '}',
    /* フィールド同士の上下隙間。 form は display:flex flex-direction:column gap:12px (デフォルト)
       なので gap を contact ページ並 (32px) に広げる */
    'form {',
    '  gap: 32px !important;',
    '}',
    /* お名前 / メール / 電話 / お問い合わせ詳細 の label は DOM 上は存在するが、 Shopify Forms
       の CSS で 視覚的に隠されている (clip / visually-hidden 等)。 強制的に block 表示にして、
       contact ページのフィールド見出しと同じ font-weight 700 + 黒 + 小余白で表示する */
    'label {',
    '  display: block !important;',
    '  visibility: visible !important;',
    '  position: static !important;',
    '  top: auto !important;',
    '  bottom: auto !important;',
    '  left: auto !important;',
    '  right: auto !important;',
    '  clip: auto !important;',
    '  clip-path: none !important;',
    '  width: auto !important;',
    '  height: auto !important;',
    '  overflow: visible !important;',
    '  margin: 0 !important;',
    '  padding: 0 !important;',
    '  white-space: normal !important;',
    '  transform: none !important;',
    '  transition: none !important;',
    '  inset: auto !important;',
    '  background: transparent !important;',
    '  background-color: transparent !important;',
    '  z-index: auto !important;',
    '  pointer-events: auto !important;',
    '}',
    /* DOM 順は input → label のため、 各フィールドコンテナを flex column-reverse にして
       ラベルを input の上に表示する。 contact ページの「見出し→入力」 と同じ視覚順にする。
       電話番号 (_formPhoneInputContainer) は内部に 国旗 selector + 番号 input container を
       横並びで持つので column-reverse は適用しない (内側の _formFieldContainer に対して
       column-reverse がかかるため番号のラベルは番号 input の上に表示される)。 */
    '[class*="_formFieldContainer"] {',
    '  display: flex !important;',
    '  flex-direction: column-reverse !important;',
    '  gap: 8px !important;',
    '  align-items: stretch !important;',
    '}',
    /* Shopify Forms は textarea のラベルを floating 表現するため、 textarea を持つ
       _formFieldContainer の ::before 擬似要素で textarea 上端に白背景を絵描き、 そこに
       label を absolute で重ねる仕組みになっている。 我々は label を input の上 (枠外) に
       配置するため、 この ::before の白背景は不要 → display:none で完全に消す */
    '[class*="_formFieldContainer"]:has(textarea)::before {',
    '  display: none !important;',
    '  content: none !important;',
    '  background: transparent !important;',
    '  height: 0 !important;',
    '}',
    /* select の矢印 span が下寄りに表示されるため、 flex 中央配置 + line-height 0 で
       縦中央に揃える。 SVG 自体も block にして余分なベースライン余白を消す */
    '[class*="_arrowDownIcon"] {',
    '  display: inline-flex !important;',
    '  align-items: center !important;',
    '  justify-content: center !important;',
    '  line-height: 0 !important;',
    '}',
    '[class*="_arrowDownIcon"] svg {',
    '  display: block !important;',
    '}',
    /* 入力フィールド: contact ページのフォーム入力 (border-bottom-only + 大きめ余白) に近い形に。
       box-sizing: border-box + min-height: 60px で input / select の高さを完全一致させる。
       font-size は 16px 以上にすることで iOS Safari の input focus 時 auto-zoom を抑制する
       (= 入力 → 確認画面 遷移時に viewport が拡大されたままになる現象を防ぐ)。 */
    'input[type="text"], input[type="email"], input[type="tel"], input[type="number"], select, [class*="_selectToggle_"] {',
    '  width: 100% !important;',
    '  border: 1px solid #cacaca !important;',
    '  border-radius: 8px !important;',
    '  padding: 12px 16px !important;',
    '  font-size: 16px !important;',
    '  line-height: 1.5 !important;',
    '  background-color: #ffffff !important;',
    '  color: #222 !important;',
    '  box-sizing: border-box !important;',
    '  min-height: 60px !important;',
    '  height: 60px !important;',
    '}',
    /* textarea は別途、 縦 218px 固定。 font-size 16px で iOS Safari の auto-zoom を抑制。 */
    'textarea {',
    '  width: 100% !important;',
    '  border: 1px solid #cacaca !important;',
    '  border-radius: 8px !important;',
    '  padding: 12px 16px !important;',
    '  font-size: 16px !important;',
    '  line-height: 1.5 !important;',
    '  background-color: #ffffff !important;',
    '  color: #222 !important;',
    '  box-sizing: border-box !important;',
    '  height: 218px !important;',
    '  min-height: 218px !important;',
    '}',
    'input:focus, textarea:focus, select:focus {',
    '  outline: 2px solid #c63030 !important;',
    '  outline-offset: 0 !important;',
    '  border-color: #c63030 !important;',
    '}',
    /* textarea の追加属性 (line-height のみ。 縦サイズは上で 218px 固定済み) */
    'textarea {',
    '  line-height: 1.6 !important;',
    '}',
    /* 送信ボタン: LP の primary CTA (about / 商品ページ下部「申し込み手続きへ」) と完全統一。
       赤背景、 rounded 30px、 stroke 2.5 の 8x14 chevron を ::before pseudo で absolute left に固定し、
       テキストは flex center で中央配置 (= LP の button-cta 大サイズ outline と同じ構造の primary 版)。 */
    'button[type="submit"] {',
    '  position: relative !important;',
    '  background-color: #c63030 !important;',
    '  color: #ffffff !important;',
    '  border-radius: 30px !important;',
    '  border: 3px solid #c63030 !important;',
    '  font-weight: 600 !important;',
    '  font-size: 18px !important;',
    '  letter-spacing: 2px !important;',
    '  line-height: 24px !important;',
    '  padding: 0 !important;',
    '  height: 72px !important;',
    '  min-height: 72px !important;',
    '  width: 100% !important;',
    '  max-width: 334px !important;',
    '  display: flex !important;',
    '  align-items: center !important;',
    '  justify-content: center !important;',
    '  margin: 0 auto !important;',
    '  cursor: pointer !important;',
    '  transition: transform 0.3s ease-out !important;',
    '}',
    'button[type="submit"]:hover {',
    '  transform: scale(1.05);',
    '}',
    /* 送信ボタン左に右向き chevron SVG (8x14、 stroke 2.5、 白)。 absolute left 28px top 50% で
       テキストとは独立して左端に固定 (LP の button-cta snippet と同じ配置)。 */
    'button[type="submit"]::before {',
    '  content: "";',
    '  position: absolute;',
    '  left: 28px;',
    '  top: 50%;',
    '  transform: translateY(-50%);',
    '  width: 8px;',
    '  height: 14px;',
    '  background-image: url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M1.75 1.75L6.25 7L1.75 12.25" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>\');',
    '  background-repeat: no-repeat;',
    '  background-position: center;',
    '  background-size: contain;',
    '}',
    /* ラベル: 太字 + 黒で contact の各フィールドラベルと統一。 select 内のラベルは
       <label> ではなく <p class="_label_...">なので両方カバーする。
       「必須」 バッジを横に並べるため inline-flex + gap で配置する */
    'label, [class*="_label_"] {',
    '  display: inline-flex !important;',
    '  align-items: center !important;',
    '  gap: 8px !important;',
    '  font-weight: 700 !important;',
    '  color: #222 !important;',
    '  font-size: 13px !important;',
    '  letter-spacing: 2px !important;',
    '  line-height: 22.5px !important;',
    '}',
    /* 必須バッジ: contact ページの span スタイルに揃える (赤枠丸 + 赤文字 + 太字) */
    '.hakotsuru-required-badge {',
    '  display: inline-block !important;',
    '  font-size: 10px !important;',
    '  color: #c63030 !important;',
    '  border: 1.5px solid #c63030 !important;',
    '  border-radius: 9999px !important;',
    '  padding: 1px 8px !important;',
    '  font-weight: 700 !important;',
    '  letter-spacing: 1px !important;',
    '  line-height: 16px !important;',
    '  background: transparent !important;',
    '}',
    /* エラーメッセージは Shopify Forms 側の表示にそのまま任せる。
       今回は font-size やマージンを触らない (過剰指定で他要素が縮む副作用を避ける)。 */
    /* エラーメッセージ ([role="alert"]) の font 統一。 select container 内 (= form 直下では
       ない) のエラーは余白触らない (アプリ既定で密接表示済み)。 form 直下のエラーは form gap
       (32px) で前後に空きが入るので、 negative margin で input 直下に詰める */
    '[role="alert"] {',
    '  font-size: 13px !important;',
    '  color: #c63030 !important;',
    '  letter-spacing: 0 !important;',
    '}',
    /* サインアップ規約文 (_formDisclaimer)。 デフォルト 10px から読みやすいサイズに上げる。 */
    '[class*="_formDisclaimer"], [class*="_formDisclaimer"] p {',
    '  font-size: 12px !important;',
    '  line-height: 1.6 !important;',
    '}',
    'form > [role="alert"] {',
    '  margin-top: -24px !important;',
    '  margin-bottom: 0 !important;',
    '}',
    /* placeholder スタイル: グレー + tracking。 各 input/textarea に共通 */
    'input::placeholder, textarea::placeholder {',
    '  color: #c0c0c0 !important;',
    '  letter-spacing: 1px !important;',
    '  opacity: 1 !important;',
    '}',
    /* select の placeholder「選択してください」。 _selectToggleText が空 (未選択) の時のみ
       擬似要素として表示。 選択後は _selectToggleText に値が入って :empty が外れ、 自動で
       消える。 他フィールドの placeholder はグレーだが、 ここだけ黒で表示する */
    '[class*="_selectToggleText_"]:empty::before {',
    '  content: "選択してください" !important;',
    '  color: #222 !important;',
    '  letter-spacing: 1px !important;',
    '  font-size: 14px !important;',
    '}',
  ].join('\n');

  /* select の矢印アイコンを contact ページの矢印 SVG (V 形 stroke、 stroke-linecap round、
     stroke #222) に置き換える。 Shopify Forms 初期出力は filled triangle (#5C5F62) のため
     見た目が contact と大きく違うので SVG 自体を outerHTML 書き換えする。 */
  var CONTACT_CHEVRON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">' +
    '<path d="M1 1.5L6 6.5L11 1.5" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  function replaceSelectArrows(sr) {
    var arrows = sr.querySelectorAll('[class*="_arrowDownIcon"]');
    arrows.forEach(function (arrow) {
      if (arrow.dataset.hakotsuruReplaced === '1') return;
      arrow.innerHTML = CONTACT_CHEVRON_SVG;
      arrow.dataset.hakotsuruReplaced = '1';
    });
  }

  /* Shopify Forms のエラーメッセージを「〇〇は必須項目です」 形式に統一する。
     アプリの default は「名は必須です」 「フィールドは必須です」 等で項目名が曖昧 (特に
     テキスト系の汎用「フィールドは必須です」 は電話番号と詳細で同じ文言になる) ので、
     alert 要素の親 container を辿って label テキストで項目名を解決する。

     メール形式エラー等の「形式エラー」 は専用の文言に置換。 */
  function customizeErrorMessages(sr) {
    /* 自前 validation で挿入した alert (data-custom-validation-error) は文言置換対象外。 */
    var alerts = sr.querySelectorAll('[role="alert"]:not([data-custom-validation-error])');
    alerts.forEach(function (a) {
      var text = (a.textContent || '').trim();
      if (!text) return;
      /* メールアドレス形式エラーを優先判定 (= 必須エラーではない、 特定パターン) */
      if (/メールアドレス|email|正しい/.test(text) && !/必須/.test(text)) {
        var newText = 'メールアドレスの形式が正しくありません';
        if (a.textContent.trim() !== newText) a.textContent = newText;
        return;
      }
      /* 必須エラー: 親 container の label を見て項目名を取得し「○○は必須項目です」 に */
      if (/必須/.test(text)) {
        var fieldName = resolveFieldNameForAlert(a);
        if (fieldName) {
          var unified = fieldName + 'は必須項目です';
          if (a.textContent.trim() !== unified) a.textContent = unified;
        }
      }
    });
  }

  /* alert 要素から項目名を解決する。
     1. _formFieldContainer 内 / _selectContainer 内に存在する場合: 同 container の label
     2. form 直下に存在する場合 (= 各 container の兄弟): 直前 sibling の container 内 label */
  function resolveFieldNameForAlert(alertEl) {
    var container = alertEl.closest('[class*="_formFieldContainer"]') ||
                    alertEl.closest('[class*="_selectContainer"]');
    if (container) {
      var lbl = container.querySelector('label') ||
                container.querySelector('[class*="_label_"]');
      if (lbl) return getLabelMainText(lbl);
    }
    /* form 直下の場合は直前の sibling コンテナを探す */
    var sibling = alertEl.previousElementSibling;
    while (sibling) {
      var lbl2 = sibling.querySelector && (
        sibling.querySelector('[class*="_formFieldContainer"] label') ||
        sibling.querySelector('label') ||
        sibling.querySelector('[class*="_label_"]')
      );
      if (lbl2) return getLabelMainText(lbl2);
      sibling = sibling.previousElementSibling;
    }
    return '';
  }

  /* Shopify Forms は focus / value 変化に応じて label に inline style (top:XXpx 等) を
     書き込んで floating 表現を再現する。 CSS で打ち消しているが、 inline style 自体も
     除去しておく方が確実。 観察 + 除去を毎回実行する。 */
  function stripLabelInlineStyle(sr) {
    var labels = sr.querySelectorAll('label');
    labels.forEach(function (l) {
      if (l.getAttribute('style')) l.removeAttribute('style');
    });
  }

  /* contact ページと同じ placeholder を各 input/textarea に設定する。
     Shopify Forms の input は name 属性が空のため、 container 内の label テキストで識別。
     既に placeholder がある場合は触らない (Admin 側で設定済みのものを尊重)。 */
  var PLACEHOLDER_MAP = {
    'お名前': '山田 太郎',
    'メールアドレス': 'example@email.com',
    '電話番号': '09012345678',
    'お問い合わせ詳細': 'お問い合わせ内容をご記入ください'
  };
  function setPlaceholders(sr) {
    var containers = sr.querySelectorAll('[class*="_formFieldContainer"]');
    containers.forEach(function (container) {
      var label = container.querySelector('label');
      if (!label) return;
      var key = getLabelMainText(label);
      var ph = PLACEHOLDER_MAP[key];
      if (!ph) return;
      var input = container.querySelector('input, textarea');
      if (input && !input.placeholder) {
        input.placeholder = ph;
      }
    });
    /* 電話番号はラベルが phone 等の場合でも placeholder を設定できるよう、 id ベースで補完する。 */
    var phoneInput = getPhoneInput(sr);
    if (phoneInput && !phoneInput.placeholder) {
      phoneInput.placeholder = PLACEHOLDER_MAP['電話番号'] || '09012345678';
    }
  }


  /* 必須バッジ (span.hakotsuru-required-badge) を各 label の末尾に挿入。 既に
     付与済みのものはスキップ。 全フィールド必須として扱う (Shopify Forms 側の required
     属性とは独立に視覚バッジを付ける)。 */
  function addRequiredBadges(sr) {
    var labels = sr.querySelectorAll('label, [class*="_label_"]');
    labels.forEach(function (l) {
      if (l.querySelector('.hakotsuru-required-badge')) return;
      var text = (l.textContent || '').trim();
      if (!text) return;
      var badge = document.createElement('span');
      badge.className = 'hakotsuru-required-badge';
      badge.textContent = '必須';
      l.appendChild(badge);
    });
  }

  /* label の見える「本文テキスト」 を取り出す (バッジ等は除いた純粋な文字列)。
     placeholder マップのキーとして使うために、 hakotsuru-required-badge は除外する。 */
  function getLabelMainText(label) {
    var clone = label.cloneNode(true);
    var badge = clone.querySelector('.hakotsuru-required-badge');
    if (badge) badge.remove();
    return (clone.textContent || '').trim();
  }

  /* 電話番号フィールドの input / container を取得する。 Shopify Admin 側のラベル (phone / 電話番号 等)
     や翻訳タイミングに依存せず、 カスタムフィールド id (custom#tel) で識別する。 ラベル文字列での
     識別はラベル変更で壊れるため、 電話番号フィールドは常に id ベースで特定する。 */
  function getPhoneInput(sr) {
    return sr.querySelector('input[id="custom#tel"]') || sr.querySelector('input[id$="#tel"]');
  }
  function getPhoneContainer(sr) {
    var inp = getPhoneInput(sr);
    return inp ? inp.closest('[class*="_formFieldContainer"]') : null;
  }

  /* フィールドの container を取得する。 電話番号は id ベース (getPhoneContainer)、 それ以外は
     ラベル文字列で識別する。 */
  function findFieldContainer(sr, fieldKey) {
    if (fieldKey === '電話番号') return getPhoneContainer(sr);
    var containers = sr.querySelectorAll('[class*="_formFieldContainer"]');
    for (var i = 0; i < containers.length; i++) {
      var lbl = containers[i].querySelector('label');
      if (lbl && getLabelMainText(lbl) === fieldKey) return containers[i];
    }
    return null;
  }

  function injectInto(embed) {
    if (!embed) return;
    var sr = embed.shadowRoot;
    if (!sr) return;
    if (!embed[INJECTED]) {
      var style = document.createElement('style');
      style.setAttribute('data-hakotsuru-forms-style', 'true');
      style.textContent = CSS;
      sr.appendChild(style);
      embed[INJECTED] = true;
    }
    /* 矢印 SVG 置換は描画後 (= shadowRoot 内に span._arrowDownIcon が現れた後) に毎回試行。
       既に置換済みのものは dataset で skip。 label の inline style 除去は毎回実行する
       (Shopify Forms が focus / blur 時に再度書き込むため)。 */
    replaceSelectArrows(sr);
    stripLabelInlineStyle(sr);
    fixPhoneLabel(sr);
    addRequiredBadges(sr);
    setPlaceholders(sr);
    fixPhoneInputType(sr);
    moveDisclaimerBeforeSubmit(sr);
    customizeErrorMessages(sr);
    setupConfirmFlow(embed);
  }

  /* 電話番号フィールドの表示ラベルを「電話番号」 に統一する。 Shopify Admin 側のラベルが「phone」
     のままのケースに備え、 id (custom#tel) で特定した container の label テキストノードを書き換える。
     必須バッジ (span) は別ノードなので影響しない。 React 再描画で戻ることがあるため、 injectInto が
     繰り返し呼ばれるたびに適用する。 */
  function fixPhoneLabel(sr) {
    var c = getPhoneContainer(sr);
    if (!c) return;
    var lbl = c.querySelector('label');
    if (!lbl) return;
    for (var i = 0; i < lbl.childNodes.length; i++) {
      var node = lbl.childNodes[i];
      if (node.nodeType === 3 && node.textContent.trim() && node.textContent.trim() !== '電話番号') {
        node.textContent = '電話番号';
      }
    }
  }

  /* サインアップ規約文 (_formDisclaimer) はデフォルトで submit ボタンの下に表示されるが、
     確認ボタンの上に出したいので submit ボタンの直前へ移動する。 React の再描画で元の位置に
     戻ることがあるため、 injectInto が MutationObserver 経由で繰り返し呼ばれるたびに、
     既に直前にいなければ移動し直す。 */
  function moveDisclaimerBeforeSubmit(sr) {
    var disclaimer = sr.querySelector('[class*="_formDisclaimer"]');
    var submitBtn = sr.querySelector('button[type="submit"]');
    if (!disclaimer || !submitBtn || !submitBtn.parentNode) return;
    if (submitBtn.previousElementSibling === disclaimer) return;
    submitBtn.parentNode.insertBefore(disclaimer, submitBtn);
  }

  /* 電話番号フィールドを「数字のみ・最大 11 桁」 入力可能にし、 フォーカスを外した時 (blur) に
     桁数をリアルタイム検証する。 Shopify 側は type=number だが、 ハイフンや指数 e が見た目だけ
     入力できてしまうため、 type=tel + inputmode=numeric にして input イベントで数字以外を除去 +
     11 桁にトリムする。 桁数 (10-11 桁) の検証は Shopify 標準にないので blur で自前表示する
     (メールの形式エラーが Shopify 組み込みでリアルタイム発火するのと挙動を揃える)。
     React は type を管理しておらず、 value 更新は native setter + input 再 dispatch で同期する。 */
  var PHONE_ERROR_MESSAGE = '電話番号は 10-11 桁の半角数字で入力してください';
  function fixPhoneInputType(sr) {
    var input = getPhoneInput(sr);
    var phoneContainer = getPhoneContainer(sr);
    if (!input || !phoneContainer || input.__phoneDigitsOnly) return;
    input.__phoneDigitsOnly = true;
    input.setAttribute('type', 'tel');
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('maxlength', '11');
    input.addEventListener('input', function () {
      /* 数字以外を除去し 11 桁でカットする (= 最大 11 桁)。 */
      var digits = input.value.replace(/\D/g, '').slice(0, 11);
      if (input.value !== digits) {
        var setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
        setter.call(input, digits);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
    input.addEventListener('blur', function () {
      /* フォーカスを外した時点で桁数を検証。 既存の電話エラーを一旦消してから再判定する。 */
      var existing = phoneContainer.querySelector('[data-custom-validation-error]');
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      var v = input.value;
      if (v && !/^\d{10,11}$/.test(v)) {
        showCustomValidationError(sr, '電話番号', PHONE_ERROR_MESSAGE);
      }
    });
  }

  /* 確認画面フロー (2 ステップ送信版):
     Shopify Forms は hCaptcha + isTrusted user gesture を要求するため、 button.click() や
     requestSubmit() による programmatic 送信は 403 エラーになる。 そこで「確認画面 → ユーザーが
     元送信ボタンを再クリック」 形式にする。

     1. 初期表示: submit button ラベル「確認する」、 確認画面 hidden、 入力欄 visible
     2. ユーザーが「確認する」 押下:
        - confirmingMode=false (= 入力モード) の場合: 自前 validation → OK なら preventDefault +
          確認画面を表示、 入力欄のみを Shadow DOM 内 CSS で hide、 submit button のラベルを
          「送信する」 に変更、 confirmingMode=true
        - confirmingMode=true (= 確認モード) の場合: そのまま素通り → Shopify Forms 標準送信
          (ユーザーの実 click なので isTrusted=true、 hCaptcha が走り 200 で submission OK)
     3. 確認画面の「修正する」: 入力欄を visible に戻し、 submit button ラベルを「確認する」 に、
        confirmingMode=false にして入力モードへ */
  function setupConfirmFlow(embed) {
    if (embed.__hakotsuruConfirmHooked) return;
    var sr = embed.shadowRoot;
    var submitBtn = sr.querySelector('button[type="submit"]');
    var confirmSection = document.querySelector('[data-forms-confirm]');
    if (!submitBtn || !confirmSection) return;
    embed.__hakotsuruConfirmHooked = true;

    /* shadowRoot に 入力欄 hide 用 <style> を作成 (確認モード時に append、 入力モード時に remove)。
       入力欄 + エラー UI + Shopify Forms のフッター (規約文等) を hide し、 各 container の
       padding/margin/gap を 0 にして送信ボタンを確認画面の直下に詰める。 */
    var fieldHideStyle = document.createElement('style');
    fieldHideStyle.setAttribute('data-hakotsuru-field-hide', 'true');
    fieldHideStyle.textContent = [
      /* 入力欄を「視覚的に隠す」 だけ。 display:none にすると Shopify Forms 内部の送信処理が
         field を参照できず glitch エラーになることがあるので避ける。 また position:absolute で
         flow から外すと flex の fieldset が content 幅 (submit テキスト幅) まで縮み、
         width:100% の送信ボタンも一緒に縮むため使わない。 flow に残したまま height:0 +
         overflow:hidden で潰し、 横幅 (= 入力欄の 100% 幅) を維持する。 */
      '[class*="_formFieldContainer"], [class*="_selectContainer"], [class*="_formPhoneInputContainer"], [role="alert"] {',
      '  visibility: hidden !important;',
      '  height: 0 !important;',
      '  min-height: 0 !important;',
      '  margin: 0 !important;',
      '  padding: 0 !important;',
      '  border: 0 !important;',
      '  overflow: hidden !important;',
      '  pointer-events: none !important;',
      '}',
      /* Shopify Forms のサインアップ規約文 (= form 末尾のテキスト) を hide */
      'form > p, form > div > p, [class*="_finePrint"], [class*="_smsLegalText"], [class*="_legalText"] {',
      '  display: none !important;',
      '}',
      /* 各 container の padding/margin/gap をリセットして submit button を確認画面に近付ける。
         さらに width:100% を付与する。 入力欄を position:absolute で flow から外すと flex の
         各コンテナが content 幅 (= submit ボタンのテキスト幅 約 86px) まで連鎖的に縮み、
         width:100% の送信ボタンも一緒に縮んでしまうため、 submit までの親チェーン全体の幅を
         維持して送信ボタンの幅 (max 334px) を確認モードでも保つ。 */
      '[class*="_appEmbedRoot"], [class*="_container_3eyaa"], [class*="_inline_"], [class*="_formContainer"], [class*="_gridItem"], [class*="_formFieldset"], form {',
      '  padding: 0 !important;',
      '  margin: 0 !important;',
      '  gap: 0 !important;',
      '  min-height: 0 !important;',
      '  width: 100% !important;',
      '  align-items: stretch !important;',
      /* _formContainer 等は overflow:hidden を持ち、 高さが詰まると送信ボタンの下端が切れる。
         確認モードでは overflow:visible にして送信ボタンが切れないようにする。 */
      '  overflow: visible !important;',
      '}',
      /* _container_3eyaa は display:grid + justify-content:center のため、 grid item (class なしの
         中間 div) が content 幅 (submit テキスト幅) に縮む。 列を 1fr + justify-items:stretch に
         して grid item を full 幅に伸ばし、 送信ボタンまでの幅を維持する。 */
      '[class*="_container_3eyaa"] {',
      '  grid-template-columns: 1fr !important;',
      '  justify-items: stretch !important;',
      '  justify-content: stretch !important;',
      '}'
    ].join('\n');

    var labelSpan = submitBtn.querySelector('span') || submitBtn;
    labelSpan.textContent = '確認する';

    var confirmingMode = false;

    var formsWrapper = document.querySelector('.hakotsuru-forms-page-apps');

    function setInputMode() {
      confirmingMode = false;
      if (fieldHideStyle.parentNode) fieldHideStyle.parentNode.removeChild(fieldHideStyle);
      confirmSection.setAttribute('data-forms-confirm-hidden', '1');
      if (formsWrapper) formsWrapper.classList.remove('hakotsuru-confirming');
      var ls = submitBtn.querySelector('span') || submitBtn;
      ls.textContent = '確認する';
      clearCustomValidationErrors(sr);
    }
    function setConfirmMode() {
      confirmingMode = true;
      sr.appendChild(fieldHideStyle);
      populateConfirmValues(sr, confirmSection);
      confirmSection.removeAttribute('data-forms-confirm-hidden');
      if (formsWrapper) formsWrapper.classList.add('hakotsuru-confirming');
      var ls = submitBtn.querySelector('span') || submitBtn;
      ls.textContent = '送信する';
      /* 確認画面 + 「送信する」 ボタンの両方が見えるように、 確認画面トップへスクロール */
      confirmSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    submitBtn.addEventListener('click', function (e) {
      if (confirmingMode) {
        /* 確認モード = 標準送信ルート。 isTrusted な実 click なので Shopify Forms 標準フロー
           (hCaptcha + POST + 完了画面) が動く。 ここで preventDefault しない。
           送信前に:
             1. light DOM 側に「送信中…」 のローディングオーバーレイを画面全体に被せる
                (= 元フォームが一瞬見えるのを防ぐ + ユーザーに処理中であることを伝える)
             2. shadowRoot から field-hide style を剥がして Shopify Forms 内部が visible
                state の field で submit 処理できるようにする (稀な glitch 回避) */
        showSubmittingOverlay();
        if (fieldHideStyle.parentNode) {
          fieldHideStyle.parentNode.removeChild(fieldHideStyle);
        }
        /* 自動返信メール用に入力値を localStorage に保存 (サンクスページの helper form が読む) */
        try {
          var email = readShadowFieldValue(sr, 'メールアドレス');
          var firstName = readShadowFieldValue(sr, 'お名前');
          var category = readShadowFieldValue(sr, 'お問い合わせ種別');
          var phone = readShadowFieldValue(sr, '電話番号');
          var detail = readShadowFieldValue(sr, 'お問い合わせ詳細');
          var noteParts = [];
          if (category) noteParts.push('お問い合わせ種別: ' + category);
          if (phone) noteParts.push('電話番号: ' + phone);
          if (detail) noteParts.push('お問い合わせ詳細:\n' + detail);
          if (email) {
            localStorage.setItem('hakotsuru_inquiry_subscribe', JSON.stringify({
              email: email,
              firstName: firstName,
              note: noteParts.join('\n\n'),
              kubun: category,
              tel: phone,
              text: detail,
              ts: Date.now()
            }));
          }
        } catch (err) {}
        return;
      }
      /* 入力モード: 自前 validation 通過時のみ確認モードに遷移、 NG なら Shopify Forms に委譲 */
      var form = sr.querySelector('form');
      if (!form) return;
      /* 自前エラー表示を先にクリアする。 early return より前に置くことで、 戻るときに
         前回の形式エラーが残り続ける不具合を防ぐ。 */
      clearCustomValidationErrors(sr);

      /* メール / 電話番号の形式チェックを必須チェックより先に行う。 必須チェックの early return
         より後ろに置くと、 他項目が未入力のときに電話の桁数エラーが発火しなくなるため、
         形式エラーは入力済みの値に対して常に評価する。 形式不正なら確認モードへ遷移させず、
         該当 input 直下にエラーメッセージを差し込んで入力モードに留める。 */
      var emailVal = readShadowFieldValue(sr, 'メールアドレス');
      var phoneVal = readShadowFieldValue(sr, '電話番号');
      var emailError = '';
      var phoneError = '';
      if (emailVal && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
        emailError = 'メールアドレスの形式が正しくありません';
      }
      if (phoneVal) {
        /* fixPhoneInputType で数字のみ入力可能にしているため、 数字 10-11 桁で検証する。 */
        if (!/^\d{10,11}$/.test(phoneVal)) {
          phoneError = PHONE_ERROR_MESSAGE;
        }
      }
      if (emailError || phoneError) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (emailError) showCustomValidationError(sr, 'メールアドレス', emailError);
        if (phoneError) showCustomValidationError(sr, '電話番号', phoneError);
        /* submit を止めると Shopify Forms 標準のエラースクロールも走らないため、 自前で
           最初のエラーフィールドへスクロールする (必須エラー時の挙動と揃える)。 */
        scrollToFieldContainer(sr, emailError ? 'メールアドレス' : '電話番号');
        return;
      }

      /* 形式 OK 後に必須チェック。 未入力があれば Shopify Forms 標準の必須エラーに委譲する。 */
      var allFilled = true;
      var fields = form.querySelectorAll('input, textarea');
      fields.forEach(function (f) {
        var type = (f.type || '').toLowerCase();
        if (type === 'hidden' || type === 'submit' || type === 'button') return;
        if (!String(f.value || '').trim()) allFilled = false;
      });
      var selectText = sr.querySelector('[class*="_selectToggleText_"]');
      if (selectText && !(selectText.textContent || '').trim()) allFilled = false;
      if (!allFilled) return;
      if (!form.checkValidity()) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      setConfirmMode();
    }, true);

    var backBtn = confirmSection.querySelector('[data-forms-back]');
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        setInputMode();
      });
    }
  }

  /* 確認画面の各 <dd data-confirm-key="..."> に shadowRoot 内の対応 input/select の現在値を
     転記する。 Shopify Forms の input は name 属性が空のため、 container 内の label テキストで
     識別する (setPlaceholders と同じパターン)。 */
  function populateConfirmValues(sr, confirmSection) {
    var dds = confirmSection.querySelectorAll('[data-confirm-key]');
    dds.forEach(function (dd) {
      var key = dd.getAttribute('data-confirm-key');
      dd.textContent = readShadowFieldValue(sr, key);
    });
  }

  /* 送信中のローディングオーバーレイ。 light DOM の body に fixed inset:0 で被せる。
     Shopify Forms の標準フローが完了 (= 完了画面表示 or リダイレクト) で自然に消える
     設計だが、 タイムアウトを設けて 15 秒後に「送信に失敗した可能性があります」 + リロード
     ボタンを表示し、 ユーザーを stuck 状態から救出する。 二重表示を防ぐため id 付きで管理。 */
  function showSubmittingOverlay() {
    if (document.getElementById('hakotsuru-submitting-overlay')) return;
    var overlay = document.createElement('div');
    overlay.id = 'hakotsuru-submitting-overlay';
    overlay.style.cssText = [
      'position: fixed',
      'inset: 0',
      'background: #f4f9f9',
      'z-index: 99999',
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'justify-content: center',
      'gap: 16px',
      'font-family: "Noto Sans JP", sans-serif',
      'font-size: 14px',
      'letter-spacing: 2px',
      'color: #222'
    ].join(';') + ';';
    var spinner = document.createElement('div');
    spinner.style.cssText = [
      'width: 32px',
      'height: 32px',
      'border: 3px solid #cacaca',
      'border-top-color: #c63030',
      'border-radius: 50%',
      'animation: hakotsuru-spin 0.8s linear infinite'
    ].join(';') + ';';
    var label = document.createElement('p');
    label.id = 'hakotsuru-submitting-label';
    label.textContent = '送信中…';
    label.style.cssText = 'margin: 0;';
    overlay.appendChild(spinner);
    overlay.appendChild(label);
    /* keyframes は head に inject */
    if (!document.getElementById('hakotsuru-spin-style')) {
      var st = document.createElement('style');
      st.id = 'hakotsuru-spin-style';
      st.textContent = '@keyframes hakotsuru-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(st);
    }
    document.body.appendChild(overlay);

    /* 15 秒経っても完了画面に遷移していなければ送信失敗扱いにして
       「もう一度試す」 ボタンを表示する */
    setTimeout(function () {
      var stillThere = document.getElementById('hakotsuru-submitting-overlay');
      if (!stillThere) return;
      spinner.style.display = 'none';
      label.textContent = '送信に失敗した可能性があります。 もう一度お試しください。';
      label.style.maxWidth = '320px';
      label.style.textAlign = 'center';
      label.style.lineHeight = '1.8';
      var retryBtn = document.createElement('button');
      retryBtn.type = 'button';
      retryBtn.textContent = 'やり直す';
      retryBtn.style.cssText = [
        'background: #c63030',
        'color: #ffffff',
        'border: 0',
        'padding: 12px 32px',
        'border-radius: 30px',
        'font-size: 14px',
        'letter-spacing: 2px',
        'font-weight: 600',
        'cursor: pointer'
      ].join(';') + ';';
      retryBtn.addEventListener('click', function () {
        window.location.reload();
      });
      overlay.appendChild(retryBtn);
    }, 15000);
  }

  /* 自前 validation エラーを該当 input の直下に表示する。
     role="alert" を付けると Shopify Forms 側の [role="alert"] 配置 CSS に巻き込まれるため、
     role は付けず aria-live でアクセシビリティを担保し、 スタイルは inline で完結させる。

     Shopify Forms の _formFieldContainer は flex-direction: column-reverse で、 DOM 順
     (input → label) が視覚的に逆転 (label が上、 input が下) する。 そのため単純な appendChild
     や input 直後挿入では視覚的に上へ回り込む。 column-reverse のときは container の先頭に
     挿入する (= 視覚的に最下部) ことで、 input の直下に表示させる。 */
  function showCustomValidationError(sr, fieldKey, message) {
    var c = findFieldContainer(sr, fieldKey);
    if (!c) return;
    var existing = c.querySelector('[data-custom-validation-error]');
    if (existing) {
      existing.textContent = message;
      return;
    }
    var el = document.createElement('p');
    el.setAttribute('aria-live', 'assertive');
    el.setAttribute('data-custom-validation-error', '1');
    el.style.cssText = 'display: block; width: 100%; margin: 4px 0 0 0; padding: 0; font-size: 13px; line-height: 1.5; color: #c63030; letter-spacing: 0;';
    el.textContent = message;
    var isReverse = getComputedStyle(c).flexDirection === 'column-reverse';
    if (isReverse) {
      c.insertBefore(el, c.firstChild);
    } else {
      c.appendChild(el);
    }
  }

  function clearCustomValidationErrors(sr) {
    var errs = sr.querySelectorAll('[data-custom-validation-error]');
    errs.forEach(function (el) { if (el.parentNode) el.parentNode.removeChild(el); });
  }

  /* 指定フィールドの container を画面内にスクロールする。 自前 validation で submit を止めた際に
     Shopify Forms 標準のエラースクロールが走らないのを補う。 */
  function scrollToFieldContainer(sr, fieldKey) {
    var c = findFieldContainer(sr, fieldKey);
    if (!c) return;
    try { c.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {
      c.scrollIntoView();
    }
  }

  function readShadowFieldValue(sr, key) {
    if (key === 'お問い合わせ種別') {
      /* select の選択値は _selectToggleText span の textContent。 未選択時は空 */
      var sel = sr.querySelector('[class*="_selectToggleText_"]');
      return sel ? (sel.textContent || '').trim() : '';
    }
    if (key === '電話番号') {
      /* 電話番号はラベルに依存せず id (custom#tel) で取得する。 */
      var phoneInput = getPhoneInput(sr);
      return phoneInput ? phoneInput.value : '';
    }
    /* お名前 / メールアドレス / お問い合わせ詳細:
       container 内の label テキストで identify する統一処理 */
    var containers = sr.querySelectorAll('[class*="_formFieldContainer"]');
    for (var i = 0; i < containers.length; i++) {
      var c = containers[i];
      var lbl = c.querySelector('label');
      if (!lbl) continue;
      if (getLabelMainText(lbl) === key) {
        var input = c.querySelector('input, textarea');
        return input ? input.value : '';
      }
    }
    return '';
  }

  function tryInject() {
    var embeds = document.querySelectorAll('shopify-forms-embed');
    embeds.forEach(injectInto);
  }

  /* 初回 + Shopify Forms の遅延ロード対策の MutationObserver。
     shopify-forms-embed の shadowRoot は要素アタッチ後に内部 DOM が描画されるまで時間がかかる
     可能性があるため、 DOMContentLoaded と subtree mutation 両方でリトライする。 */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInject);
  } else {
    tryInject();
  }
  /* shadowRoot 自体が遅延作成される場合に備え、 短間隔で複数回試行 */
  var attempts = 0;
  var iv = setInterval(function () {
    tryInject();
    attempts++;
    if (attempts > 20) clearInterval(iv);
  }, 200);

  /* Shopify Forms が dynamic に再マウントするケースに備え、 ホスト要素の追加を監視 */
  if (window.MutationObserver) {
    var mo = new MutationObserver(function () { tryInject(); });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* focus / input / blur 時に Shopify Forms が label.style を再設定するため、 shadowRoot 内の
     その種イベントを capture して即座に inline style を剥がす + 矢印再置換 + エラー文言置換。
     さらに shadowRoot 内 DOM の変化を MutationObserver で監視して、 新規に追加されたエラー
     メッセージにも即座に置換を効かせる。 */
  function attachShadowListeners() {
    var embeds = document.querySelectorAll('shopify-forms-embed');
    embeds.forEach(function (embed) {
      var sr = embed.shadowRoot;
      if (!sr || sr.__hakotsuruListening) return;
      sr.__hakotsuruListening = true;
      ['focusin', 'focusout', 'input', 'change'].forEach(function (evt) {
        sr.addEventListener(evt, function () {
          /* マイクロタスク後に label style を再除去 (Shopify Forms の同イベント handler が
             label.style を書いた直後に走ることを期待) */
          setTimeout(function () {
            stripLabelInlineStyle(sr);
            replaceSelectArrows(sr);
            customizeErrorMessages(sr);
          }, 0);
        }, true);
      });
      if (window.MutationObserver) {
        var smo = new MutationObserver(function () {
          customizeErrorMessages(sr);
          stripLabelInlineStyle(sr);
        });
        smo.observe(sr, { childList: true, subtree: true, characterData: true });
      }
    });
  }
  var listenerAttempts = 0;
  var liv = setInterval(function () {
    attachShadowListeners();
    listenerAttempts++;
    if (listenerAttempts > 20) clearInterval(liv);
  }, 200);
})();
